"use client";

import type { LanguageOrAccent } from "@mw/shared/languagesAndAccents";
import type { LanguagesAccentsGlobeBlock } from "@mw/shared/payload-types";
import type { Key } from "react-aria-components";

import { Button } from "@components/core/Button";
import { CornerMarkerDot } from "@components/core/CornerMarkerDot";
import { DottedBorder } from "@components/core/GradientBorder";
import { BaseLink } from "@components/core/Link";
import { TabList } from "@components/core/TabList";
import DynamicFlag from "@components/languages/DynamicFlag";
import IconArrowRight from "@ui/central-icons/icons/IconArrowRight";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import ChevronLeft16 from "@ui/icons/16x16/chevron-left-16.svg";
import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import { ttsLanguages, ttsAccents } from "@mw/shared/languagesAndAccents";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { useHover } from "react-aria";
import { cva } from "class-variance-authority";
import { clsx } from "clsx";
import { Tabs, TabPanel } from "react-aria-components";

import { NativeAudioProvider, useNativeAudio } from "@/app/_providers/native-audio/NativeAudioProvider";
import type { LanguageAudioSample } from "@/app/_ui/components/core/Globe/Globe";
import { PlayPauseButton } from "@/app/_ui/components/core/PlayPauseButton";

import { Block } from "../Block";
import dynamic from "next/dynamic";

const LazyGlobeInner = dynamic(() => import("@/app/_ui/components/core/Globe/Globe").then(mod => mod.GlobeInner));
import { LanguagesAccentsModal } from "./LanguagesAccentsModal";

type Tab = "languages" | "accents";

const DEFAULT_ITEMS_PER_PAGE = 6;

type ItemMode = "text-to-speech-link" | "none";

export interface LanguageItemProps {
  item: LanguageOrAccent;
  showDottedBorder?: boolean;
  fullWidth?: boolean;
  mode?: ItemMode;
  previewUrl?: string;
}

export const LanguageItemWithHover = (props: LanguageItemProps) => {
  if (props.mode === "none") {
    return <LanguageItemStatic {...props} />;
  }
  return <LanguageItemWithAudio {...props} />;
};

const LanguageItemStatic = ({ item, showDottedBorder = true, fullWidth = false }: LanguageItemProps) => {
  const t = useTranslations("ClientKeys.languages_accents_globe");

  return (
    <li>
      <div className={clsx("tw-relative tw-flex tw-items-center tw-gap-3 tw-py-4", fullWidth ? "tw-px-6" : "tw-pr-3")}>
        {showDottedBorder && <DottedBorder side='bottom' />}
        <DynamicFlag
          alt={t("flag_alt", { language: item.label })}
          className='tw-h-9 tw-w-9 tw-shrink-0 tw-rounded-full'
          locale={item.value}
          size={36}
        />
        <span className='tw-flex-1 f-paragraph-02'>{item.label}</span>
      </div>
    </li>
  );
};

const LanguageItemWithAudio = ({ item, showDottedBorder = true, fullWidth = false, previewUrl }: LanguageItemProps) => {
  const t = useTranslations("ClientKeys.languages_accents_globe");
  const { togglePlay, playingId } = useNativeAudio();
  const isItemPlaying = playingId === item.slug;
  const { isHovered: rowHovered, hoverProps } = useHover({});

  const flag = (
    <DynamicFlag
      alt={t("flag_alt", { language: item.label })}
      className='tw-h-9 tw-w-9 tw-shrink-0 tw-rounded-full'
      locale={item.value}
      size={36}
    />
  );

  // Has preview URL: row click plays audio, name text links out
  if (previewUrl) {
    return (
      <li>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- Row click is a mouse convenience; keyboard users reach BaseLink and PlayPauseButton directly */}
        <div
          {...hoverProps}
          onClick={() => togglePlay({ id: item.slug, url: previewUrl })}
          className={clsx(
            "tw-group tw-relative tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-py-4 tw-transition-colors hover:tw-bg-[linear-gradient(90deg,_rgba(255,_255,_255,_0)_0%,_#FFFFFF_8%,_#FFFFFF_92%,_rgba(255,_255,_255,_0)_100%)]",
            fullWidth ? "tw-px-6" : "tw-pr-3",
          )}
        >
          {showDottedBorder && <DottedBorder side='bottom' />}
          {flag}
          <span className='tw-flex-1 f-paragraph-02'>
            <BaseLink
              href={`/text-to-speech/${item.slug}`}
              onClick={e => e.stopPropagation()}
              className='tw-underline tw-underline-offset-2 tw-decoration-gray-300 tw-transition-colors hover:tw-decoration-current'
            >
              {item.label}
            </BaseLink>
          </span>
          <PlayPauseButton
            isPlaying={isItemPlaying}
            variant={rowHovered ? "primary" : "elevated"}
            size='pill'
            iconSize='sm'
            onClick={e => {
              e.stopPropagation();
              togglePlay({ id: item.slug, url: previewUrl });
            }}
            aria-label={isItemPlaying ? t("pause_preview") : t("play_preview", { language: item.label })}
          />
        </div>
      </li>
    );
  }

  // No preview URL: full row link with chevron arrow
  return (
    <li>
      <BaseLink
        href={`/text-to-speech/${item.slug}`}
        className={clsx(
          "tw-group tw-relative tw-flex tw-items-center tw-gap-3 tw-py-4 tw-transition-colors hover:tw-bg-[linear-gradient(90deg,_rgba(255,_255,_255,_0)_0%,_#FFFFFF_8%,_#FFFFFF_92%,_rgba(255,_255,_255,_0)_100%)]",
          fullWidth ? "tw-px-6" : "tw-pr-3",
        )}
      >
        {showDottedBorder && <DottedBorder side='bottom' />}
        {flag}
        <span className='tw-flex-1 f-paragraph-02'>
          <span className='tw-underline tw-underline-offset-2 tw-decoration-gray-300 tw-transition-colors group-hover:tw-decoration-current'>
            {item.label}
          </span>
        </span>
        <span className='tw-sr-only'> {t("link_suffix")}</span>
        <div
          className='tw-shrink-0 tw-text-gray-400 tw-transition-colors tw-duration-300 group-hover:tw-text-black tw-mr-2'
          aria-hidden='true'
        >
          <IconArrowRight className={clsx(iconStrokeFix, "tw-h-4 tw-w-4")} />
        </div>
      </BaseLink>
    </li>
  );
};

interface PaginatedListProps {
  items: LanguageOrAccent[];
  pageSize: number;
  currentPage: number;
  label: string;
  mode?: ItemMode;
  previewUrls?: Record<string, string>;
}

/**
 * A horizontally-scrolling container that keeps ALL items in the DOM for SEO.
 * Each "page" is a full-width section, and we translateX to show the current page.
 * Inactive pages are hidden from assistive technology with aria-hidden and inert.
 */
const PaginatedList = ({ items, pageSize, currentPage, label, mode, previewUrls }: PaginatedListProps) => {
  const t = useTranslations("ClientKeys.languages_accents_globe");
  const totalPages = Math.ceil(items.length / pageSize);

  // Split items into pages
  const pages = useMemo(() => {
    const result: LanguageOrAccent[][] = [];
    for (let i = 0; i < totalPages; i++) {
      result.push(items.slice(i * pageSize, (i + 1) * pageSize));
    }
    return result;
  }, [items, pageSize, totalPages]);

  // Guard against empty items to avoid division by zero
  if (items.length === 0) {
    return null;
  }

  // Calculate the translateX percentage based on current page
  // Each page takes up (100 / totalPages)% of the container width
  const translateXPercent = -currentPage * (100 / totalPages);

  return (
    <div className='tw-relative tw-overflow-hidden'>
      {/* Label */}
      <p className='tw-mb-4 tw-text-gray-500 f-paragraph-03'>{label}</p>

      {/* Live region for page change announcements */}
      <div aria-live='polite' aria-atomic='true' className='tw-sr-only'>
        {totalPages > 1 && t("page_announcement", { current: currentPage + 1, total: totalPages })}
      </div>

      {/* Sliding container - translateX based on current page */}
      <m.div
        className='tw-flex'
        animate={{ x: `${translateXPercent}%` }}
        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
        style={{ width: `${totalPages * 100}%` }}
      >
        {pages.map((pageItems, pageIndex) => {
          const isActive = pageIndex === currentPage;
          return (
            <ul
              key={pageIndex}
              className='tw-flex tw-flex-col'
              style={{ width: `${100 / totalPages}%` }}
              aria-hidden={!isActive}
              inert={!isActive ? true : undefined}
            >
              {pageItems.map((item, itemIndex) => (
                <LanguageItemWithHover
                  key={item.value}
                  item={item}
                  showDottedBorder={itemIndex < pageItems.length - 1}
                  mode={mode}
                  previewUrl={previewUrls?.[item.slug]}
                />
              ))}
            </ul>
          );
        })}
      </m.div>
    </div>
  );
};

type LanguagesAccentsGlobeProps = LanguagesAccentsGlobeBlock & {
  previewUrls?: Record<string, string>;
  languageAudioSamples?: LanguageAudioSample[];
};

/**
 * Sorts items with featured ones first (in order), then remaining alphabetically.
 */
function sortWithFeatured<T extends { value: string; label: string }>(items: T[], featured?: string[] | null): T[] {
  if (!featured || featured.length === 0) {
    return items;
  }

  const featuredSet = new Set(featured);
  const featuredItems: T[] = [];
  const remainingItems: T[] = [];

  // Separate featured and remaining
  for (const item of items) {
    if (featuredSet.has(item.value)) {
      featuredItems.push(item);
    } else {
      remainingItems.push(item);
    }
  }

  // Sort featured by the order they appear in the featured array
  featuredItems.sort((a, b) => featured.indexOf(a.value) - featured.indexOf(b.value));

  // Sort remaining alphabetically
  remainingItems.sort((a, b) => a.label.localeCompare(b.label));

  return [...featuredItems, ...remainingItems];
}

const switcherAlignmentStyles = cva("tw-flex tw-mb-10", {
  variants: {
    alignment: {
      left: "tw-justify-start",
      center: "tw-justify-center",
      right: "tw-justify-end",
    },
  },
  defaultVariants: {
    alignment: "center",
  },
});

export function BlockLanguagesAccentsGlobe({
  mode = "text-to-speech-link",
  showSwitcher = true,
  switcherAlignment,
  defaultTab,
  featuredLanguages,
  languages: selectedLanguages,
  featuredAccents,
  accents: selectedAccents,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  previewUrls,
  languageAudioSamples,
}: LanguagesAccentsGlobeProps) {
  const alignment = switcherAlignment ?? "center";
  const t = useTranslations("ClientKeys.languages_accents_globe");
  const tGlobals = useTranslations("ClientKeys.Globals");

  const [activeTab, setActiveTab] = useState<Tab>(defaultTab ?? "languages");
  const [languagesPage, setLanguagesPage] = useState(0);
  const [accentsPage, setAccentsPage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter languages based on selection, or use all if not specified
  // Then sort with featured first, remaining alphabetically
  const languagesList = useMemo(() => {
    let filtered = ttsLanguages;
    if (selectedLanguages && selectedLanguages.length > 0) {
      filtered = ttsLanguages.filter(lang => selectedLanguages.includes(lang.value as any));
    }
    return sortWithFeatured(filtered, featuredLanguages as string[] | undefined);
  }, [selectedLanguages, featuredLanguages]);

  // Filter accents based on selection, or use all if not specified
  // Then sort with featured first, remaining alphabetically
  const accentsList = useMemo(() => {
    let filtered = ttsAccents;
    if (selectedAccents && selectedAccents.length > 0) {
      filtered = ttsAccents.filter(accent => selectedAccents.includes(accent.value as any));
    }
    return sortWithFeatured(filtered, featuredAccents as string[] | undefined);
  }, [selectedAccents, featuredAccents]);

  const pageSize = itemsPerPage || DEFAULT_ITEMS_PER_PAGE;
  const languagesTotalPages = Math.ceil(languagesList.length / pageSize);
  const accentsTotalPages = Math.ceil(accentsList.length / pageSize);

  const currentPage = activeTab === "languages" ? languagesPage : accentsPage;
  const totalPages = activeTab === "languages" ? languagesTotalPages : accentsTotalPages;
  const showPagination = totalPages > 1;

  // Pagination handlers
  const handlePrevPage = () => {
    if (activeTab === "languages") {
      setLanguagesPage(prev => Math.max(0, prev - 1));
    } else {
      setAccentsPage(prev => Math.max(0, prev - 1));
    }
  };

  const handleNextPage = () => {
    if (activeTab === "languages") {
      setLanguagesPage(prev => Math.min(languagesTotalPages - 1, prev + 1));
    } else {
      setAccentsPage(prev => Math.min(accentsTotalPages - 1, prev + 1));
    }
  };

  // Handle tab change from React Aria
  const handleTabChange = (key: Key) => {
    setActiveTab(key as Tab);
  };

  // Shared controls for pagination and modal opener
  const ControlsSection = (
    <div className='tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-p-1'>
      {showPagination && (
        <div className='tw-flex tw-items-center tw-gap-2 tw-order-1 lg:tw-order-2'>
          <Button
            variant='elevated'
            size='pill'
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            aria-label={tGlobals("previous")}
            icon={<ChevronLeft16 />}
          />
          <Button
            variant='elevated'
            size='pill'
            onClick={handleNextPage}
            disabled={currentPage >= totalPages - 1}
            aria-label={tGlobals("next")}
            icon={<ChevronRight16 />}
          />
        </div>
      )}
      <Button
        variant='elevated'
        size='lg'
        onClick={() => setIsModalOpen(true)}
        aria-label={t("view_all_button_aria")}
        className={showPagination ? "tw-order-2 lg:tw-order-1" : ""}
      >
        {t("view_all_button")}
      </Button>
    </div>
  );

  // Shared Globe section
  const GlobeSection = (
    <>
      <div className='tw-relative tw-hidden lg:tw-block'>
        <div className='tw-h-full tw-w-px bg-overlay-dark' />
        <CornerMarkerDot position='topCentered' />
        <CornerMarkerDot position='bottomCentered' />
      </div>
      <div className='tw-flex tw-w-full tw-items-center tw-justify-center tw-p-6 lg:tw-w-1/2'>
        <div className='tw-h-[300px] tw-w-[300px] md:tw-h-[400px] md:tw-w-[400px] lg:tw-h-[450px] lg:tw-w-[450px]'>
          <LazyGlobeInner languageAudioSamples={languageAudioSamples} />
        </div>
      </div>
    </>
  );

  return (
    <NativeAudioProvider>
      <Block className='container' name='languages-accents-globe'>
        <div className='tw-flex tw-flex-col tw-items-center'>
          {showSwitcher ? (
            /*
             * React Aria Tabs for full accessibility:
             * - Keyboard navigation (Arrow keys, Home, End)
             * - ARIA roles (tablist, tab, tabpanel)
             * - shouldForceMount keeps all content in DOM for SEO
             * - Motion wrapper provides sliding animation
             */
            <Tabs selectedKey={activeTab} onSelectionChange={handleTabChange} className='tw-w-full'>
              {/* Tab switcher using pre-built TabList component */}
              <div className={switcherAlignmentStyles({ alignment })}>
                <TabList
                  aria-label={t("tabs_aria_label")}
                  items={[
                    { id: "languages", title: t("tab_languages") },
                    { id: "accents", title: t("tab_accents") },
                  ]}
                  surface='cream'
                  size='md'
                />
              </div>

              {/* Solid horizontal separator */}
              <div className='tw-mb-0 tw-w-full border-t border-overlay-dark' />

              {/* Main content: List + Globe */}
              <div className='tw-flex tw-w-full tw-flex-col tw-gap-10 lg:tw-flex-row lg:tw-gap-0'>
                <div className='tw-w-full lg:tw-w-1/2 tw-px-[0.5rem] md:tw-px-[1rem]'>
                  <div className='tw-relative tw-overflow-hidden tw-py-5'>
                    {/*
                     * Sliding animation container.
                     * TabPanels with shouldForceMount keep content in DOM for SEO.
                     * React Aria handles aria-hidden and inert on inactive panels.
                     */}
                    <m.div
                      className='tw-flex'
                      animate={{ x: activeTab === "languages" ? "0%" : "-50%" }}
                      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                      style={{ width: "200%" }}
                    >
                      <TabPanel id='languages' shouldForceMount className='tw-w-1/2'>
                        <PaginatedList
                          items={languagesList}
                          pageSize={pageSize}
                          currentPage={languagesPage}
                          label={t("most_popular_languages")}
                          mode={mode ?? "text-to-speech-link"}
                          previewUrls={previewUrls}
                        />
                      </TabPanel>
                      <TabPanel id='accents' shouldForceMount className='tw-w-1/2'>
                        <PaginatedList
                          items={accentsList}
                          pageSize={pageSize}
                          currentPage={accentsPage}
                          label={t("most_popular_accents")}
                          mode={mode ?? "text-to-speech-link"}
                          previewUrls={previewUrls}
                        />
                      </TabPanel>
                    </m.div>

                    {ControlsSection}
                  </div>
                </div>

                {GlobeSection}
              </div>
            </Tabs>
          ) : (
            /* No switcher - just languages list with Globe */
            <div className='tw-flex tw-w-full tw-flex-col tw-gap-10 lg:tw-flex-row lg:tw-gap-0'>
              <div className='tw-w-full lg:tw-w-1/2 lg:tw-pr-10'>
                <div className='tw-relative tw-py-5'>
                  <PaginatedList
                    items={languagesList}
                    pageSize={pageSize}
                    currentPage={languagesPage}
                    label={t("most_popular_languages")}
                    mode={mode ?? "text-to-speech-link"}
                    previewUrls={previewUrls}
                  />
                  {ControlsSection}
                </div>
              </div>
              {GlobeSection}
            </div>
          )}
        </div>

        {/* Modal for all languages/accents - always shows complete lists */}
        <LanguagesAccentsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          initialTab={activeTab}
          languages={ttsLanguages}
          accents={ttsAccents}
          mode={mode ?? "text-to-speech-link"}
          previewUrls={previewUrls}
        />
      </Block>
    </NativeAudioProvider>
  );
}
